import { Link } from "react-router-dom";
import { Images, Inbox, KeyRound, Lightbulb, Megaphone, Settings2, ShoppingBag, Star, Users } from "lucide-react";

const STATUS_HELP = [
  ["New", "Just came in. Reach out with a quote."],
  ["Quoted", "You sent the price. Waiting on payment."],
  ["Paid", "Payment received. The order is confirmed."],
  ["In Production", "You're working on it."],
  ["Completed", "Picked up or delivered."],
  ["Cancelled", "The order isn't moving forward."],
];

const Section = ({ icon: Icon, title, id, children }) => (
  <section className="rounded-3xl bg-white p-6 ring-1 ring-[#EBD08A] sm:p-8" data-testid={`help-section-${id}`}>
    <div className="flex items-center gap-3">
      <span className="grid h-11 w-11 place-items-center rounded-2xl bg-[#0E0B0D] text-[#D4A537]"><Icon className="h-5 w-5" /></span>
      <h2 className="font-display text-xl font-bold sm:text-2xl">{title}</h2>
    </div>
    <div className="mt-5 space-y-3 text-[15px] leading-relaxed text-black/75">{children}</div>
  </section>
);

const Steps = ({ items }) => (
  <ol className="space-y-2">
    {items.map((t, i) => (
      <li key={i} className="flex gap-3"><span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#FF1F8E] text-xs font-bold text-white">{i + 1}</span><span>{t}</span></li>
    ))}
  </ol>
);

export default function AdminHelp() {
  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="admin-help-page">
      <div>
        <p className="font-script text-4xl text-[#FF1F8E]">Welcome!</p>
        <h1 className="font-display text-3xl font-bold">How to use your admin</h1>
        <p className="mt-2 text-black/60">Everything you need to run your website: orders, photos, reviews, promotions and site info. It all works on your phone too.</p>
      </div>

      <Section icon={Inbox} title="Order requests" id="requests">
        <p>Every time someone sends a custom order request, it shows up in <Link to="/admin" className="font-semibold text-[#FF1F8E]">Requests</Link>, and you also get an email at kmkcustomsllc@yahoo.com.</p>
        <Steps items={[
          "Tap a request to see the details: product, quantity, sizes, design idea, date needed, preferred payment and inspiration photos.",
          "Tap the phone number or email to contact the customer with your quote.",
          "Change the status from the drop-down as the order moves along. It saves right away.",
          "Use Private notes for things like the quote amount or pickup plans, then tap Save notes. Customers never see these notes.",
        ]} />
        <div className="mt-4 overflow-hidden rounded-2xl ring-1 ring-[#EBD08A]">
          {STATUS_HELP.map(([s, t]) => (
            <div key={s} className="flex gap-4 border-b border-[#EBD08A]/50 px-4 py-2.5 text-sm last:border-0">
              <span className="w-28 shrink-0 font-semibold text-[#0E0B0D]">{s}</span><span>{t}</span>
            </div>
          ))}
        </div>
        <p className="text-sm">Use the filter buttons at the top (New, Quoted, Paid…) to see only the orders in that stage.</p>
      </Section>

      <Section icon={Users} title="Customers & payments" id="customers">
        <p>Customers can order as a guest or with an account. If they're signed in, they can see their order's status on their "My Orders" page, so keeping the status up to date keeps them in the loop.</p>
        <p>The website never takes payment. Customers choose Venmo, Apple Pay, Cash App, Chime or Relay. <b>For Relay, send your bank transfer details privately</b> (text or email) with the quote. Never post them publicly.</p>
      </Section>

      <Section icon={Images} title="Gallery photos" id="gallery">
        <Steps items={[
          "Open Gallery and tap Choose file to pick a photo from your phone.",
          "Pick the category it belongs in (Yard Signs, School Spirit, etc.) and write a short caption.",
          "Tap Upload. It appears on the Gallery page right away, and the newest photos also show on the homepage.",
          "To take a photo down, tap the trash icon under it.",
        ]} />
        <p className="text-sm"><b>Photo tip:</b> natural light, a plain background and the design filling most of the picture make your work shine.</p>
      </Section>

      <Section icon={Star} title="Customer reviews" id="reviews">
        <Steps items={[
          "When a customer says something kind (Facebook comment, text, DM), open Reviews.",
          "Enter their name, what they ordered, paste their words and tap the stars.",
          "Optionally add a photo of their order, then tap Add review.",
        ]} />
        <p className="text-sm">Your newest 6 reviews show on the homepage. The section stays hidden until you add your first one. It's polite to ask the customer before posting their review.</p>
      </Section>

      <Section icon={ShoppingBag} title="Shop & online payments" id="shop">
        <p>The <Link to="/admin/shop" className="font-semibold text-[#FF1F8E]">Shop</Link> tab holds your set-price items (like the sweatshirt deals). Customers choose color, size, design and shipping or pickup, see the total, and pay online by card or Apple Pay. Paid shop orders arrive in Requests already marked <b>Paid</b>, and you get a "PAID" email.</p>
        <Steps items={[
          "New item: name, price, and the pieces in it (e.g. Sweatshirt + T-Shirt) with their sizes and colors.",
          "Set the plus-size fee, personalization fee, your shipping price, and whether free pickup is offered.",
          "Add your designs at the bottom so customers can tap one, or they can describe their own.",
          "For custom orders: open the request, type the Final price and tap Send pay link. When they pay, it turns Paid by itself.",
          "Paid with Relay, Venmo, Cash App or Chime? Use Record a payment on the order.",
        ]} />
      </Section>

      <Section icon={Megaphone} title="Promotions & holiday specials" id="promotions">
        <p>Use <Link to="/admin/promotions" className="font-semibold text-[#FF1F8E]">Promos</Link> for things like Thanksgiving and Christmas specials. Every promotion that's turned on shows on the homepage and Services page.</p>
        <Steps items={[
          "Tap New promotion. Add a title, a short description, and prices if you have them (e.g. 1 tee $25).",
          "Add a picture, and a link if you use a Google Form (or leave it empty to send people to your order form).",
          "Optional: set 'Start showing on' and 'Stop showing after' dates. You can prepare Christmas in November and it appears and disappears by itself.",
          "Turn the switch On and tap Save. Turn it Off any time to hide it.",
        ]} />
        <p className="text-sm"><b>Tip:</b> instead of deleting last year's special, turn it off. Next year, edit the prices and dates and turn it back on.</p>
      </Section>

      <Section icon={Settings2} title="Site info: banner, contact & page text" id="site-info">
        <p>In <Link to="/admin/site" className="font-semibold text-[#FF1F8E]">Site Info</Link> you can change the pink top banner (or hide it), your phone, email and social links, the payment options customers can pick, the About story, ordering policy and the Services descriptions.</p>
        <p className="text-sm">Make your changes, then tap <b>Save changes</b> at the bottom. They show on the website right away.</p>
      </Section>

      <Section icon={KeyRound} title="Logging in & staying safe" id="login">
        <p>Log in any time at <b>your website address + /admin/login</b> (or tap "Owner login" at the very bottom of any page). Tap Log out when you're on a shared device.</p>
        <p>Keep your password private. After 5 wrong tries, logins pause for 15 minutes for security.</p>
      </Section>

      <div className="flex gap-3 rounded-3xl bg-[#0E0B0D] p-6 text-white" data-testid="help-tip">
        <Lightbulb className="h-6 w-6 shrink-0 text-[#D4A537]" />
        <p className="text-sm text-white/80"><b className="text-white">Daily habit:</b> check the <b>New</b> filter each morning, reply with quotes, and move paid orders to <b>In Production</b>. Your customers will love the quick updates!</p>
      </div>
    </div>
  );
}
